import React, { useState, useEffect } from "react";
import { ArrowRight, Clock, FileSearch, Printer, Shield, Network, GraduationCap } from "lucide-react";
import { formatGain } from "../data/questions";

const ICON_MAP = { FileSearch, Printer, Shield, Network, GraduationCap };

const QuestionScreen = ({ question, questionIndex, totalQuestions, onAnswer, runningGain }) => {
  const [selected, setSelected] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    setSelected(null);
    const t = setTimeout(() => setVisible(true), 60);
    return () => clearTimeout(t);
  }, [question.id]);

  const Icon = ICON_MAP[question.icon] || Shield;
  const previewGain = runningGain + (selected ? selected.gain_minutes : 0);
  const progressPct = ((questionIndex) / totalQuestions) * 100 + 20;

  const handleNext = () => {
    if (selected) onAnswer(selected);
  };

  return (
    <div className="screen-container" style={{ paddingTop: 20 }}>
      <div className="content-wrapper">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
          <img
            src="/logos._lbs_conecti.png"
            alt="Groupe LBS Conecti"
            style={{
              display: "block",
              margin: "0 left",
              width: "80%",
              height: "auto",
            }}
          />
          </div>
          {previewGain > 0 && (
            <div
              data-testid="gain-counter"
              className="flex items-center gap-1.5 rounded-full px-3 py-1.5"
              style={{
                background: "rgba(181,22,58,0.18)",
                border: "1px solid rgba(181,22,58,0.4)",
              }}
            >
              <Clock size={12} color="#B5163A" />
              <span
                style={{
                  color: "#B5163A",
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: "Inter, sans-serif",
                }}
              >
                +{formatGain(previewGain)} / jour
              </span>
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="mb-5">
          <div className="flex justify-between mb-1.5">
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, fontFamily: "Inter, sans-serif" }}>
              Question {questionIndex + 1} / {totalQuestions}
            </span>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, fontFamily: "Inter, sans-serif" }}>
              {question.category}
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: 5,
              backgroundColor: "#1F4E6D",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${progressPct}%`,
                backgroundColor: "#B5163A",
                borderRadius: 4,
                transition: "width 0.5s ease",
              }}
            />
          </div>
        </div>

        {/* Content */}
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateX(0)" : "translateX(16px)",
            transition: "opacity 0.3s ease, transform 0.3s ease",
          }}
        >
          {/* Question card */}
          <div className="card p-6 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  backgroundColor: "#12354D",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon size={22} color="#B5163A" />
              </div>
              <div>
                <p
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#9CA3AF",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontFamily: "Inter, sans-serif",
                    marginBottom: 2,
                  }}
                >
                  {question.category}
                </p>
                <h3
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#12354D",
                  }}
                >
                  {question.title}
                </h3>
              </div>
            </div>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                color: "#4A4A4A",
                lineHeight: 1.6,
                fontWeight: 500,
              }}
            >
              {question.question}
            </p>
          </div>

          {/* Options */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
            {question.options.map((option) => {
              const isSelected = selected?.label === option.label;
              return (
                <button
                  key={option.label}
                  className="option-card"
                  data-testid={`option-${option.label.toLowerCase()}`}
                  onClick={() => setSelected(option)}
                >
                  <div className={`option-inner${isSelected ? " selected" : ""}`}>
                    <div
                      className="option-badge"
                      style={{
                        backgroundColor: isSelected ? "#B5163A" : "#F7F4F0",
                        color: isSelected ? "#FFFFFF" : "#4A4A4A",
                      }}
                    >
                      {option.label}
                    </div>
                    <p
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        color: "#4A4A4A",
                        lineHeight: 1.55,
                        paddingTop: 2,
                      }}
                    >
                      {option.text}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Next button */}
          <button
            data-testid="next-button"
            className="btn-primary"
            onClick={handleNext}
            disabled={!selected}
          >
            {questionIndex === totalQuestions - 1
              ? "Voir mes résultats"
              : "Question suivante"}
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionScreen;
